import { useEffect, useRef, useState } from "react";
import type { TaxScenario, TaxScenarioInput, TaxResults } from "@/tax/types";
import { calculateTaxServer } from "@/tax/taxApi";
import { buildStrategyList } from "@/lib/strategyList";
import { formatCurrency, formatPercent, formatChartCurrency, formatCompactCurrency, formatMultiple } from "@/tax/format";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, LabelList, AreaChart, Area, ReferenceLine, ReferenceDot, CartesianGrid, Customized, Cell, ComposedChart, Line } from "recharts";
import { Printer, Plus, GripVertical, FileSpreadsheet, Loader2, Presentation } from "lucide-react";
import StrategyStoryDialog from "@/components/StrategyStoryDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RothProjectionChart } from "@/components/RothProjectionChart";
import { useRothProjectionGate } from "@/hooks/useRothProjectionGate";
import { FIA_STRATEGIES, type RothGrowthModel } from "@/tax/rothGrowth";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { generateComparisonPDF } from "@/lib/pdfExport";
import { generateComparisonReport } from "@/lib/comparisonReport";
import { toast } from "sonner";
import { ExportPickDialog } from "@/components/ExportPickDialog";
import { generateComparisonExcel } from "@/lib/excelExport";
import { METRICS } from "@/lib/comparisonMetrics";
import ComparisonV4 from "@/components/ComparisonV4";
import { useWithoutAltResults } from "@/hooks/useWithoutAltResults";
import { leversOf, moveTitle } from "@/lib/comparisonStory";
import { repeatCostsFor, type RepeatCosts } from "@/lib/repeatCosts";
import type { ExportView } from "@/lib/exportView";
import { buildStrategyJourney, payoffHorizonYears, journeyHorizon, buildRepeatLadder, altGrossReturnMultiple, altAnnualizedReturn, JOURNEY_ROTH_SPREAD_YEARS, type StrategyJourney, type JourneyPoint } from "@/tax/strategyJourney";

interface ComparisonTableProps {
  scenarios: TaxScenario[];
  onAddStrategy?: (baseline: TaxScenario) => void;
  onBack?: () => void;
  /**
   * Called when the user drags a strategy chip in the reorder bar.
   * `orderedIds` reflects the new full order — the first entry is the baseline.
   */
  onReorder?: (orderedIds: string[]) => void;
  /** Engine call used to score the strategy with its alt removed (repeat-year cost). Tests inject a stub. */
  computeWithoutAlt?: (input: TaxScenarioInput) => Promise<TaxResults>;
}


const COMPARE_COLORS = [
  "hsl(var(--chart-federal))",
  "hsl(var(--chart-state))",
  "hsl(var(--chart-fica))",
  "hsl(var(--chart-amt))",
  "hsl(var(--chart-niit))",
];

function formatVal(value: number, format: "currency" | "percent") {
  return format === "currency" ? formatCurrency(value) : formatPercent(value);
}

function growthModelLabel(model: RothGrowthModel | undefined): string {
  if (!model || model === 'straight') return 'Custom Growth Rate';
  return FIA_STRATEGIES[model].label;
}

/**
 * Strips the client-name prefix from a scenario name and returns the strategy
 * suffix in parens. e.g. "TEst 2 (Roth + Alt)" → "Roth + Alt".
 * Falls back to the full name if no parens are present.
 */
function strategySuffix(name: string): string {
  const m = name.match(/\(([^)]+)\)\s*$/);
  return m ? m[1] : name;
}

function DiffCell({
  base,
  current,
  format,
  direction,
}: {
  base: number;
  current: number;
  format: "currency" | "percent";
  direction: "lower-is-better" | "higher-is-better";
}) {
  const diff = current - base;
  if (Math.abs(diff) < 0.01) return <span className="text-muted-foreground">—</span>;
  const isUp = diff > 0;
  const isGood = direction === "lower-is-better" ? !isUp : isUp;
  return (
    <span className={`font-mono text-sm font-semibold ${isGood ? "text-success" : "text-destructive"}`}>
      {isUp ? "▲ +" : "▼ "}
      {formatVal(Math.abs(diff), format)}
    </span>
  );
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border bg-card px-3 py-2 shadow-lg text-xs">
      <p className="font-semibold mb-1">{label}</p>
      {payload.map((p: any, i: number) => (
        <p key={i} className="font-mono" style={{ color: p.fill }}>
          {p.name}: {formatCurrency(p.value)}
        </p>
      ))}
    </div>
  );
};

// ── Pay the tax, or put it to work (three doors) ──────────────────────────────
// One card per strategy, in the order Steve narrates it: pay the tax, run the strategy
// this year, let it play out to the payoff horizon. The doors follow whichever levers
// the strategy actually moved off the baseline — Roth, LTCG, alt, or any combination —
// so an alt-only strategy shows only the alt and a Roth + alt strategy shows both. The
// five-years strip only appears when an alt is in play. Every figure is engine output.

function Stat({ label, value, tone, big }: { label: string; value: string; tone?: "good" | "bad" | "muted"; big?: boolean }) {
  const color =
    tone === "good" ? "text-success" : tone === "bad" ? "text-destructive" : tone === "muted" ? "text-muted-foreground" : "";
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className={`font-mono font-bold ${big ? "text-2xl" : "text-lg"} ${color}`}>{value}</p>
    </div>
  );
}

const signed = (v: number) => `${v >= 0 ? "+" : "−"}${formatCurrency(Math.abs(v))}`;

function Door({ eyebrow, title, hero, heroLabel, heroTone, lead, rows }: {
  eyebrow: string; title: string; hero: string; heroLabel: string; heroTone?: "good" | "bad";
  lead?: boolean; rows: Array<[string, string, ("good" | "bad")?]>;
}) {
  return (
    <div className={`rounded-lg p-5 flex flex-col gap-3 ${lead ? "border-2 border-primary bg-card shadow-lg" : "border bg-card/50"}`}>
      <div>
        <p className={`text-[10px] uppercase tracking-widest ${lead ? "text-primary" : "text-muted-foreground"}`}>{eyebrow}</p>
        <p className="font-semibold text-base leading-tight">{title}</p>
      </div>
      <div className="border-y py-3">
        <Stat label={heroLabel} value={hero} tone={heroTone} big />
      </div>
      <div className="space-y-1.5 text-xs">
        {rows.map(([l, v, tone]) => (
          <div key={l} className="flex justify-between gap-2">
            <span className="text-muted-foreground">{l}</span>
            <span className={`font-mono font-semibold ${tone === "good" ? "text-success" : tone === "bad" ? "text-destructive" : ""}`}>{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function altYearsOf(s: TaxScenario): number {
  return Math.max(0, (s.results.hpeCashFlows?.length ?? 1) - 1);
}

/** The alt's own cost over baseline in a repeat year, per strategy — `repeatCostsFor`, kept in state. */
function useRepeatCosts(
  scenarios: TaxScenario[],
  baseScenario: TaxScenario | undefined,
  compute: (input: TaxScenarioInput) => Promise<TaxResults>,
  enabled: boolean,
): RepeatCosts {
  const [costs, setCosts] = useState<RepeatCosts>({});
  const key = enabled && baseScenario
    ? scenarios.filter((s) => s.id !== baseScenario.id && s.input.alternativeInvestmentAmount > 0).map((s) => `${s.id}:${s.results.totalEconomicCost}`).join("|")
    : "";
  useEffect(() => {
    if (!enabled || !baseScenario) return;
    let cancelled = false;
    repeatCostsFor(scenarios, baseScenario, compute).then((next) => { if (!cancelled) setCosts(next); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return costs;
}

function AltChoiceCards({ scenarios, baseScenario, repeatCosts }: { scenarios: TaxScenario[]; baseScenario: TaxScenario; repeatCosts: RepeatCosts }) {
  const strategies = scenarios.filter((s) => s.id !== baseScenario.id && leversOf(s, baseScenario).any);
  if (strategies.length === 0) return null;

  return (
    <Card className="shadow-lg border-0 bg-gradient-to-br from-card to-card/80" data-tour="comparison-doors">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">Pay the tax, or put it to work</CardTitle>
        <p className="text-xs text-muted-foreground">
          The same tax bill three ways: pay it, run the strategy this year, and let it play out.
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {strategies.map((s) => {
          const levers = leversOf(s, baseScenario);
          const inv = levers.alt;
          const taxSavings = baseScenario.results.totalTaxLiability - s.results.totalTaxLiability;
          const cashBack = levers.alt ? s.results.hpeNetCashReturned || 0 : 0;
          const moic = inv > 0 ? cashBack / inv : 0;
          const altYears = altYearsOf(s);
          const horizon = payoffHorizonYears([baseScenario, s]);
          const flows = levers.alt ? s.results.hpeCashFlows ?? [] : [];
          let bigYear = 0;
          for (let t = 1; t < flows.length; t++) if (flows[t] > (flows[bigYear] ?? -Infinity) || bigYear === 0) bigYear = t;
          const futureTaxAvoided = levers.roth ? s.results.estimatedFutureTaxAvoided || 0 : 0;
          const netLife = taxSavings + cashBack - inv + futureTaxAvoided;
          const repeatCost = repeatCosts[s.id];
          const ladder = repeatCost === undefined ? null : buildRepeatLadder(s, baseScenario, { repeatCostOverBaseline: repeatCost });

          const payRows: Array<[string, string, ("good" | "bad")?]> = [];
          const moveRows: Array<[string, string, ("good" | "bad")?]> = [];
          if (levers.roth) {
            payRows.push(["Roth converted", formatCurrency(0)]);
            moveRows.push(["Roth converted", formatCurrency(Math.abs(levers.roth))]);
          }
          if (levers.ltcg) {
            payRows.push(["Gains realized", formatCurrency(0)]);
            moveRows.push(["Gains realized", formatCurrency(Math.abs(levers.ltcg))]);
          }
          if (levers.alt) {
            payRows.push(["Alt investment", formatCurrency(0)]);
            payRows.push(["Alt deduction", formatCurrency(0)]);
            moveRows.push(["Alt investment", formatCurrency(inv)]);
            moveRows.push(["Alt deduction", formatCurrency(s.results.hpeImpliedDeduction)]);
          }
          payRows.push(["Tax saved", formatCurrency(0)]);
          moveRows.push(["Tax saved", signed(taxSavings), taxSavings >= 0 ? "good" : "bad"]);
          if (levers.alt) {
            moveRows.push([
              taxSavings - inv >= 0 ? "Extra cash in your pocket" : "Out of pocket",
              signed(taxSavings - inv),
              taxSavings - inv >= 0 ? "good" : "bad",
            ]);
          }

          const payoffRows: Array<[string, string, ("good" | "bad")?]> = [
            ["Tax saved this year", signed(taxSavings), taxSavings >= 0 ? "good" : "bad"],
          ];
          if (levers.alt) {
            payoffRows.push(["Cash back from the alt", signed(cashBack), "good"]);
            payoffRows.push(["Less the investment", signed(-inv), "bad"]);
          }
          if (futureTaxAvoided) payoffRows.push(["Future taxes avoided", signed(futureTaxAvoided), "good"]);
          if (levers.alt) payoffRows.push(["Multiple on investment", `${moic.toFixed(2)}×`]);
          if (levers.alt && bigYear > 0) payoffRows.push(["Biggest year", `Yr ${bigYear} · ${formatCurrency(flows[bigYear])}`]);
          if (levers.roth && s.results.rothFutureValue > 0) {
            payoffRows.push([`Roth value at Year ${horizon}`, formatCurrency(s.results.rothFutureValue)]);
          }

          return (
            <div key={s.id} className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                {strategies.length > 1 ? <p className="text-sm font-semibold">{strategySuffix(s.input.name)}</p> : <span />}
                {levers.alt > 0 && (
                  <span className="text-xs text-muted-foreground rounded-full bg-accent px-3 py-1">
                    Alt schedule · {moic.toFixed(2)}× over {altYears} years
                  </span>
                )}
              </div>
              <div className="grid gap-3 md:grid-cols-3">
                <Door
                  eyebrow="Door one" title="Pay the tax"
                  hero={formatCurrency(baseScenario.results.totalTaxLiability)} heroLabel="Federal + state, owed this year"
                  rows={payRows}
                />
                <Door
                  lead eyebrow="Door two" title={moveTitle(levers)}
                  hero={formatCurrency(s.results.totalTaxLiability)} heroLabel="Federal + state, owed this year"
                  rows={moveRows}
                />
                <Door
                  eyebrow="Door three" title={`Let it run ${horizon} years`}
                  hero={signed(netLife)} heroLabel={`Net worth increase by Year ${horizon}`}
                  heroTone={netLife >= 0 ? "good" : "bad"}
                  rows={payoffRows}
                />
              </div>
              {ladder && (
                <div className="rounded-lg border bg-card/50 p-4 space-y-3">
                  <div className="flex flex-wrap items-baseline justify-between gap-3">
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">And if you did it five years running</p>
                      <p className="text-sm font-semibold">
                        {levers.roth
                          ? `Convert once, then ${formatCurrency(inv)} into the alt every year for ${ladder.cohorts} years. Every year finishes its own schedule.`
                          : `${formatCurrency(inv)} into the alt every year for ${ladder.cohorts} years. Every year finishes its own schedule.`}
                      </p>
                    </div>
                    <div className="flex items-baseline gap-5 text-xs text-muted-foreground">
                      <span>In <span className="font-mono font-semibold text-foreground">{formatCurrency(ladder.totalInvested)}</span></span>
                      <span>Back <span className="font-mono font-semibold text-foreground">{formatCurrency(ladder.totalCashBack)}</span></span>
                      {ladder.totalRothTaxAvoided > 0 && (
                        <span>Roth taxes avoided <span className="font-mono font-semibold text-foreground">{formatCurrency(ladder.totalRothTaxAvoided)}</span></span>
                      )}
                      <span className={`font-mono font-bold text-xl ${ladder.netBenefit >= 0 ? "text-success" : "text-destructive"}`}>{signed(ladder.netBenefit)}</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-5 gap-1.5">
                    {Array.from({ length: ladder.cohorts }, (_, i) => (
                      <div key={i} className="rounded-md px-2.5 py-2 text-[11px]" style={{ background: "#D97706", opacity: 0.45 + i * 0.13, color: "#fff" }}>
                        <div className="flex justify-between font-mono font-semibold">
                          <span>Yr {i} → {i + altYears}</span>
                          <span>{signed(i === 0 ? ladder.totalCashBack / ladder.cohorts + ladder.totalRothTaxAvoided - (ladder.totalCostOverBaseline - ladder.repeatCostOverBaseline * (ladder.cohorts - 1)) : ladder.totalCashBack / ladder.cohorts - ladder.repeatCostOverBaseline)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    Year one is this strategy as saved. Years two through five repeat only the alt on the client's regular income, scored by the engine. Assumes the same income and deduction room each year. Hypothetical, not a projection.
                  </p>
                </div>
              )}
            </div>
          );
        })}
        <p className="text-[11px] text-muted-foreground">
          Fund cash follows the scenario's alt schedule scaled to the investment. Equity is carried at cost. Hypothetical, not a guarantee.
        </p>
      </CardContent>
    </Card>
  );
}

function PaybackSchedule({ scenarios, baseScenario }: { scenarios: TaxScenario[]; baseScenario: TaxScenario }) {
  const hpeScenarios = scenarios.filter((s) => s.input.alternativeInvestmentAmount > 0);
  if (hpeScenarios.length === 0) return null;
  const periodYears = Math.max(...hpeScenarios.map(journeyHorizon));

  return (
    <Card className="shadow-lg border-0 bg-gradient-to-br from-card to-card/80" data-tour="comparison-alt-takeaways">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">Alt Key Takeaways</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="w-full">
          <table className="w-full caption-bottom text-sm">
            <TableHeader>
              <TableRow className="border-b-2">
                <TableHead className="sticky left-0 top-12 bg-card z-20 min-w-[180px] font-semibold">Year</TableHead>
                {hpeScenarios.map((s) => (
                  <TableHead key={s.id} className="sticky top-12 bg-card z-10 text-center min-w-[150px] font-semibold">
                    {s.id === baseScenario.id ? 'Baseline' : strategySuffix(s.input.name)}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow className="border-t-2 bg-muted/10">
                <TableCell className="sticky left-0 bg-card z-10 text-sm font-semibold">Investment</TableCell>
                {hpeScenarios.map((s) => (
                  <TableCell key={s.id} className="text-center text-sm font-mono font-semibold">
                    {formatCurrency(s.input.alternativeInvestmentAmount)}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow className="bg-muted/10">
                <TableCell className="sticky left-0 bg-card z-10 text-sm font-semibold">Total Gross Return</TableCell>
                {hpeScenarios.map((s) => (
                  <TableCell key={s.id} className="text-center text-sm font-mono font-semibold text-success">
                    {formatCurrency(s.results.hpeFutureValue)}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow className="bg-muted/10">
                <TableCell className="sticky left-0 bg-card z-10 text-sm font-semibold">Gross Return ({periodYears}-Yr)</TableCell>
                {hpeScenarios.map((s) => (
                  <TableCell key={s.id} className="text-center text-sm font-mono font-semibold">
                    {formatMultiple(altGrossReturnMultiple(s))}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow className="bg-muted/10">
                <TableCell className="sticky left-0 bg-card z-10 text-sm font-semibold">Annualized Return ({periodYears}-Yr)</TableCell>
                {hpeScenarios.map((s) => (
                  <TableCell key={s.id} className="text-center text-sm font-mono font-semibold">
                    {formatPercent(altAnnualizedReturn(s))}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow className="bg-muted/10">
                <TableCell className="sticky left-0 bg-card z-10 text-sm font-semibold">Tax Savings</TableCell>
                {hpeScenarios.map((s) => {
                  const taxSavings = baseScenario.results.totalTaxLiability - s.results.totalTaxLiability;
                  return (
                    <TableCell key={s.id} className="text-center text-sm font-mono font-semibold text-success">
                      {formatCurrency(taxSavings)}
                    </TableCell>
                  );
                })}
              </TableRow>
              {hpeScenarios.some((s) => s.results.estimatedFutureTaxAvoided > 0) && (
                <TableRow className="bg-muted/10">
                  <TableCell className="sticky left-0 bg-card z-10 text-sm font-semibold">
                    Est. Future Taxes Avoided
                  </TableCell>
                  {hpeScenarios.map((s) => (
                    <TableCell key={s.id} className="text-center text-sm font-mono font-semibold text-success">
                      {s.results.estimatedFutureTaxAvoided > 0
                        ? formatCurrency(s.results.estimatedFutureTaxAvoided)
                        : "—"}
                    </TableCell>
                  ))}
                </TableRow>
              )}
              <TableRow className="border-t-2 bg-success/10">
                <TableCell className="sticky left-0 bg-card z-10 text-sm font-bold">Net Benefit</TableCell>
                {hpeScenarios.map((s) => {
                  const inv = s.input.alternativeInvestmentAmount;
                  const taxSavings = baseScenario.results.totalTaxLiability - s.results.totalTaxLiability;
                  const netBenefit = taxSavings + s.results.hpeFutureValue - inv + s.results.estimatedFutureTaxAvoided;
                  return (
                    <TableCell key={s.id} className="text-center">
                      <span
                        className={`text-xl font-bold font-mono ${netBenefit >= 0 ? "text-success" : "text-destructive"}`}
                      >
                        {formatCurrency(netBenefit)}
                      </span>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableBody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

export default function ComparisonTable({ scenarios, onAddStrategy, onBack, onReorder, computeWithoutAlt = calculateTaxServer }: ComparisonTableProps) {
  const [exporting, setExporting] = useState(false);
  const [storyOpen, setStoryOpen] = useState(false);
  const { isEnabled } = useFeatureFlags();
  // The doors are a new surface, not a change to an existing one, so they ship dark
  // behind 'alt_doors'. With the flag off nothing renders and the per-strategy
  // without-alt engine call never fires.
  const doorsEnabled = isEnabled('alt_doors');
  const repeatCosts = useRepeatCosts(scenarios, scenarios[0], computeWithoutAlt, doorsEnabled);
  // The exports print this page as it reads right now (Drew, 2026-10-05): journey cards
  // or paired bars. The doors are not printed; the v3 page replaces them.
  const journeyEnabled = isEnabled('cost_benefit_journey');
  const exportView: ExportView = { journey: journeyEnabled };
  // The reworked page (Drew, 2026-09-30): one story, one formula, five sections. With
  // the flag off nothing below changes; with it on the old cards do not render at all.
  const v3Enabled = isEnabled('comparison_v3');
  const withoutAlt = useWithoutAltResults(scenarios, scenarios[0], computeWithoutAlt, v3Enabled);
  const [pickOpen, setPickOpen] = useState(false);

  useEffect(() => {
    if (scenarios.length < 2 && onBack) {
      onBack();
    }
  }, [scenarios.length, onBack]);

  // Tracks which chip is currently being dragged in the strategy reorder bar.
  const dragIdRef = useRef<string | null>(null);

  const handleChipDrop = (targetId: string) => {
    const fromId = dragIdRef.current;
    dragIdRef.current = null;
    if (!fromId || !onReorder || fromId === targetId) return;
    const currentOrder = scenarios.map((s) => s.id);
    // Baseline (first scenario) is locked — never move it, never drop before it
    if (fromId === currentOrder[0] || targetId === currentOrder[0]) return;
    const fromIdx = currentOrder.indexOf(fromId);
    const toIdx = currentOrder.indexOf(targetId);
    if (fromIdx === -1 || toIdx === -1) return;
    const next = [...currentOrder];
    next.splice(fromIdx, 1);
    next.splice(toIdx, 0, fromId);
    onReorder(next);
  };

  if (scenarios.length < 2) {
    return null;
  }

  const baseScenario = scenarios[0];

  const hasAnyRoth = scenarios.some((s) => (s.input.rothConversionAmount ?? 0) > 0);
  // Key by s.id so scenarios that share a display name (e.g. two "Roth + Alt"
  // variants) don't collapse into a single bar. The display name is passed to
  // each <Bar> via the `name` prop so the legend and tooltip still read right.
  const barChartData = [
    { name: "Total Tax", ...Object.fromEntries(scenarios.map((s) => [s.id, s.results.totalTaxLiability])) },
    {
      name: "Total Economic Cost",
      ...Object.fromEntries(scenarios.map((s) => [s.id, s.results.totalEconomicCost])),
    },
    ...(hasAnyRoth
      ? [
          {
            name: "Roth Conversion",
            ...Object.fromEntries(scenarios.map((s) => [s.id, s.input.rothConversionAmount ?? 0])),
          },
        ]
      : []),
  ];

  // One horizon for every payoff label, journey cards or legacy bars: the 8-year floor, or the
  // alt schedule's last year / the Roth's growth years when either runs further.
  const horizonYears = payoffHorizonYears(scenarios);
  const costVsReturnData = scenarios.slice(1).map((s) => {
    const altReturned = s.results.hpeFutureValue || 0;
    const futureTaxAvoided = s.results.estimatedFutureTaxAvoided || 0;
    const yearNReturn = altReturned + futureTaxAvoided;
    const costOverBaseline = s.results.totalEconomicCost - baseScenario.results.totalEconomicCost;
    return {
      scenario: strategySuffix(s.input.name),
      "Year 1 Cost Over Baseline": costOverBaseline,
      [`Year ${horizonYears} Gross Return`]: yearNReturn,
      netBenefit: yearNReturn - costOverBaseline,
    };
  });

  // With v4 on, Export PDF first asks which strategies to print, then prints the v4 report.
  const pickNames = scenarios.slice(1).map((s, i) => `Strategy ${i + 1} · ${strategySuffix(s.input.name)}`);
  const handlePickedExport = (picked: number[]) => {
    setExporting(true);
    try {
      generateComparisonReport(scenarios, withoutAlt, picked);
      setPickOpen(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Couldn't build the PDF.");
    } finally {
      setExporting(false);
    }
  };

  const handleExportPDF = async () => {
    if (v3Enabled) { setPickOpen(true); return; }
    setExporting(true);
    try {
      await generateComparisonPDF(scenarios, exportView);
    } finally {
      setExporting(false);
    }
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      await generateComparisonExcel(scenarios, exportView);
    } finally {
      setExporting(false);
    }
  };
  const reportDate = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  const orderPills = onReorder ? (
        <div className="flex flex-wrap items-center gap-2 print:hidden" data-tour="comparison-order">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider mr-1">
            Strategy Order
          </span>
          {scenarios.map((s, i) => {
            const isBaseline = i === 0;
            return (
              <div
                key={s.id}
                draggable={!isBaseline}
                onDragStart={isBaseline ? undefined : (e) => {
                  dragIdRef.current = s.id;
                  e.dataTransfer.effectAllowed = "move";
                }}
                onDragOver={isBaseline ? undefined : (e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                }}
                onDrop={isBaseline ? undefined : (e) => {
                  e.preventDefault();
                  handleChipDrop(s.id);
                }}
                onDragEnd={isBaseline ? undefined : () => {
                  dragIdRef.current = null;
                }}
                className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors ${
                  isBaseline
                    ? "border-secondary/50 bg-secondary/10 cursor-default"
                    : "border-border bg-muted/40 hover:bg-muted cursor-grab active:cursor-grabbing"
                }`}
                title={isBaseline ? "Baseline is always first" : "Drag to reorder"}
              >
                {!isBaseline && <GripVertical className="h-3 w-3 text-muted-foreground" />}
                <span className="font-medium">{isBaseline ? 'Baseline' : strategySuffix(s.input.name)}</span>
                {isBaseline && <span className="text-[10px] text-secondary font-semibold">BASELINE</span>}
              </div>
            );
          })}
        </div>
  ) : null;
  return (
    <div id="comparison-print-area" className="space-y-4">
      <div className="print-report-header">
        <h1>TaxGenie Comparison Report</h1>
        <p>
          Generated {reportDate}
        </p>
      </div>

      {/* v4 (Drew, 2026-11-04): the buttons share the strategy-order row and take the
          size of the page's own small controls, so the top of the page is one row. */}
      {(() => {
        const btn = v3Enabled ? "h-7 gap-1.5 px-2.5 text-xs" : "gap-2";
        const icon = v3Enabled ? "h-3.5 w-3.5" : "h-4 w-4";
        return (
          <div className={v3Enabled ? "flex flex-wrap items-center justify-between gap-x-4 gap-y-2 print:hidden" : "flex flex-col sm:flex-row sm:items-center justify-end gap-3 print:hidden"} data-testid="comparison-top-row">
            {v3Enabled && orderPills}
            <div className={`flex flex-wrap items-center gap-2${v3Enabled ? " ml-auto" : ""}`} data-tour="comparison-export">
              {onAddStrategy && (
                <Button variant="outline" size="sm" onClick={() => onAddStrategy(baseScenario)} className={btn} data-tour="comparison-add-strategy">
                  <Plus className={icon} />
                  Add Strategy
                </Button>
              )}
              {isEnabled('strategy_story') && (
                <Button variant="outline" size="sm" onClick={() => setStoryOpen(true)} className={btn} data-tour="comparison-present">
                  <Presentation className={icon} />
                  Present
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={handleExportPDF} disabled={exporting} className={btn} data-tour="comparison-export-pdf">
                {exporting ? <Loader2 className={`${icon} animate-spin`} /> : <Printer className={icon} />}
                <span className="hidden sm:inline">Export</span> PDF
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportExcel} disabled={exporting} className={btn} data-tour="comparison-export-excel">
                {exporting ? <Loader2 className={`${icon} animate-spin`} /> : <FileSpreadsheet className={icon} />}
                <span className="hidden sm:inline">Export</span> Excel
              </Button>
            </div>
          </div>
        );
      })()}

      {v3Enabled && (
        <ExportPickDialog open={pickOpen} onOpenChange={setPickOpen} names={pickNames} busy={exporting} onExport={handlePickedExport} />
      )}

      {isEnabled('strategy_story') && (
        <StrategyStoryDialog scenarios={scenarios} open={storyOpen} onOpenChange={setStoryOpen} />
      )}

      {!v3Enabled && orderPills}

      {v3Enabled ? (
        <ComparisonV4 scenarios={scenarios} withoutAlt={withoutAlt} />
      ) : (
      <>
      {scenarios.length >= 2 && (
        <Card className="shadow-lg border-0 bg-gradient-to-br from-card to-card/80" data-tour="comparison-net-benefit">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Est. Future Value Economic Difference</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {scenarios.map((s, i) => {
                if (i === 0) {
                  return (
                    <div key={s.id} className="text-center p-4 rounded-lg bg-muted/30">
                      <p className="text-xs text-muted-foreground mb-1">
                        Baseline <span className="text-secondary">(Baseline)</span>
                      </p>
                      <p className="text-2xl font-bold font-mono text-muted-foreground">—</p>
                    </div>
                  );
                }
                const taxSavings = baseScenario.results.totalTaxLiability - s.results.totalTaxLiability;
                const investmentReturns =
                  s.results.hpeFutureValue > 0 ? s.results.hpeFutureValue - s.input.alternativeInvestmentAmount : 0;
                const futureTaxesAvoided = s.results.estimatedFutureTaxAvoided;
                const netBenefit = taxSavings + investmentReturns + futureTaxesAvoided;
                const isRoth = (s.input.rothConversionAmount ?? 0) > 0;
                return (
                  <div
                    key={s.id}
                    className={`p-4 rounded-lg ${netBenefit >= 0 ? "bg-success/10" : "bg-destructive/10"}`}
                  >
                    <p className="text-xs text-muted-foreground mb-1 text-center">{strategySuffix(s.input.name)}</p>
                    <p
                      className={`text-2xl font-bold font-mono mb-3 text-center ${netBenefit >= 0 ? "text-success" : "text-destructive"}`}
                    >
                      {netBenefit >= 0 ? "+" : ""}
                      {formatCurrency(netBenefit)}
                    </p>
                    <div className="space-y-1 text-xs border-t border-border/40 pt-2">
                      <div className="flex justify-between gap-2">
                        <span className="text-muted-foreground">Tax Savings</span>
                        <span className="font-mono font-semibold">{formatCurrency(taxSavings)}</span>
                      </div>
                      {taxSavings < 0 && isRoth && (
                        <p className="text-[10px] text-muted-foreground italic leading-tight">
                          Roth conversion increases Year 1 taxes
                        </p>
                      )}
                      <div className="flex justify-between gap-2">
                        <span className="text-muted-foreground">Alt Investment Returns</span>
                        <span className="font-mono font-semibold">{formatCurrency(investmentReturns)}</span>
                      </div>
                      <div className="flex justify-between gap-2">
                        <span className="text-muted-foreground">Est. Future Taxes Avoided</span>
                        <span className="font-mono font-semibold">{formatCurrency(futureTaxesAvoided)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {scenarios.length >= 2 && (
        <Card className="shadow-lg border-0 bg-gradient-to-br from-card to-card/80" data-tour="comparison-strategies">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Strategies Applied</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {scenarios.map((s, i) => {
                const isBaseline = i === 0;
                const strategies = isBaseline ? [] : buildStrategyList(s.input, baseScenario.input);
                return (
                  <div key={s.id} className="p-4 rounded-lg bg-muted/30">
                    <p className="text-xs text-muted-foreground mb-2 text-center">
                      {isBaseline ? 'Baseline' : strategySuffix(s.input.name)}
                      {isBaseline && <span className="text-secondary"> (Baseline)</span>}
                    </p>
                    <div className="flex justify-between gap-2 text-xs border-t border-border/40 pt-2">
                      <span className="text-muted-foreground">AGI</span>
                      <span className="font-mono font-semibold">{formatCurrency(s.results.adjustedGrossIncome)}</span>
                    </div>
                    {isBaseline || strategies.length === 0 ? (
                      <p className="text-center text-xs text-muted-foreground italic mt-2">
                        {isBaseline ? "No changes" : "Matches baseline"}
                      </p>
                    ) : (
                      <div className="space-y-1 text-xs mt-1">
                        {strategies.map((item, idx) => (
                          <div key={idx} className="flex justify-between gap-2">
                            <span className="text-muted-foreground">{item.label}</span>
                            <span className="font-mono font-semibold">{item.value}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <DetailView
        scenarios={scenarios}
        baseScenario={baseScenario}
        barChartData={barChartData}
        costVsReturnData={costVsReturnData}
        horizonYears={horizonYears}
        reportDate={reportDate}
      />
      {doorsEnabled && <AltChoiceCards scenarios={scenarios} baseScenario={baseScenario} repeatCosts={repeatCosts} />}
      <PaybackSchedule scenarios={scenarios} baseScenario={baseScenario} />
      </>
      )}
    </div>
  );
}

// ── Per-strategy journey cards (Cost Benefit view) ───────────────────────────
// Side-by-side small multiples on one shared y-scale: each card is one strategy's
// cumulative position vs. baseline, red while out of pocket, green once ahead.
// Every point comes from that scenario's engine output via buildStrategyJourney.

function JourneyTooltip({ active, payload }: { active?: boolean; payload?: Array<{ payload: JourneyPoint }> }) {
  if (!active || !payload || payload.length === 0) return null;
  const pt = payload[0].payload;
  return (
    <div className="rounded-md border bg-card px-3 py-2 text-xs shadow-md">
      <p className="font-semibold mb-1">Year {pt.year}</p>
      <p className="font-mono font-semibold">
        {pt.position >= 0 ? "+" : "-"}
        {formatCurrency(Math.abs(pt.position))} vs baseline
      </p>
      {pt.altCashBack > 0 && (
        <p className="text-muted-foreground">
          Alt cash back so far: <span className="font-mono">{formatCurrency(pt.altCashBack)}</span>
        </p>
      )}
      {pt.rothTaxAvoided > 0 && (
        <p className="text-muted-foreground">
          Roth taxes avoided: <span className="font-mono">{formatCurrency(pt.rothTaxAvoided)}</span>
        </p>
      )}
    </div>
  );
}

/**
 * `forPrint` renders the same cards for the PDF export (src/lib/journeyCapture.tsx copies each
 * chart's SVG into the file): no breakeven dot, since the line crossing zero says it on paper
 * (Drew, 2026-10-22), and no walkthrough anchor on the hidden copy.
 */
export function StrategyJourneyCards({ scenarios, horizon, forPrint = false }: { scenarios: TaxScenario[]; horizon: number; forPrint?: boolean }) {
  const baseline = scenarios[0];
  const strategies = scenarios.slice(1);
  const journeys: StrategyJourney[] = strategies.map((s) => buildStrategyJourney(s, baseline, horizon));

  // One shared y-domain so the cards compare honestly side by side.
  const allPositions = journeys.flatMap((j) => j.points.map((p) => p.position));
  const rawMax = Math.max(0, ...allPositions);
  const rawMin = Math.min(0, ...allPositions);
  const yMax = rawMax === 0 ? 1 : rawMax * 1.08;
  // Headroom on top only. The bottom sits on the real low so the bottom label reads the
  // actual out-of-pocket (−$144K), not a padded −$155K (Andrew, 2026-09-23).
  const yMin = rawMin === 0 ? -(yMax * 0.05) : rawMin;
  // Where $0 sits inside the shared domain, top-relative — splits the red/green fill.
  const zeroOffset = Math.max(0, Math.min(1, yMax / (yMax - yMin)));
  const ticks = Array.from({ length: horizon + 1 }, (_, i) => i);

  return (
    <div className="grid gap-4 lg:grid-cols-2" data-tour={forPrint ? undefined : "comparison-journey"}>
      {strategies.map((s, i) => {
        const j = journeys[i];
        const stroke = COMPARE_COLORS[(i + 1) % COMPARE_COLORS.length];
        const gradId = `journey-fill-${j.scenarioId}`;
        // "Net", not "Put in": the figure is the alt less the tax saved, and "Put in $100.2K"
        // read as the investment on a $233K alt (Andrew, 2026-09-23).
        const costLabel =
          j.costOverBaseline >= 0
            ? `Net out of pocket ${formatCompactCurrency(j.costOverBaseline)}`
            : `Saves ${formatCompactCurrency(Math.abs(j.costOverBaseline))} in Year 1`;
        const breakevenLabel =
          j.breakevenYear === null
            ? `Not whole by Yr ${j.horizonYears}`
            : j.breakevenYear === 0
              ? "Ahead from day one"
              : `Whole again ~Yr ${Math.ceil(j.breakevenYear)}`;
        return (
          <div key={j.scenarioId} className="rounded-lg border bg-card/50 p-3" data-journey-card={j.scenarioId}>
            <div className="flex flex-wrap items-baseline justify-between gap-2 px-1 pb-1">
              <div>
                <p className="font-semibold text-sm">{strategySuffix(s.input.name)}</p>
                <p className="text-xs text-muted-foreground">
                  {costLabel} → {formatCompactCurrency(j.grossReturn)} back by Yr {j.horizonYears} · {breakevenLabel}
                </p>
              </div>
              <p
                className="font-mono font-bold text-xl"
                style={{ color: j.netBenefit >= 0 ? "hsl(var(--success))" : "hsl(var(--destructive))" }}
              >
                {j.netBenefit >= 0 ? "+" : "-"}
                {formatChartCurrency(Math.abs(j.netBenefit))}
              </p>
            </div>
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={j.points} margin={{ top: 10, right: 12, bottom: 0, left: 0 }}>
                {/* Flat bands, not a fade: an even translucent green fill above the baseline and
                    a barely-there red wash below it, split exactly at $0. The gradient MUST be in
                    userSpaceOnUse units spanning the plot area's pixel height — the default
                    objectBoundingBox units measure against the filled shape's own box, which
                    puts the split at the wrong height and paints a red stripe under the baseline
                    even where the curve is above it. Customized hands us the plot offset. */}
                <Customized
                  component={(p: { offset?: { top?: number; height?: number } }) => {
                    const top = p.offset?.top ?? 0;
                    const height = p.offset?.height ?? 0;
                    return (
                      <defs>
                        <linearGradient
                          id={gradId}
                          gradientUnits="userSpaceOnUse"
                          x1="0"
                          y1={top}
                          x2="0"
                          y2={top + height}
                        >
                          <stop offset="0" stopColor="hsl(var(--success))" stopOpacity={0.28} />
                          <stop offset={zeroOffset} stopColor="hsl(var(--success))" stopOpacity={0.28} />
                          <stop offset={zeroOffset} stopColor="hsl(var(--destructive))" stopOpacity={0.1} />
                          <stop offset="1" stopColor="hsl(var(--destructive))" stopOpacity={0.1} />
                        </linearGradient>
                      </defs>
                    );
                  }}
                />
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted-foreground))" opacity={0.15} vertical={false} />
                <XAxis
                  dataKey="year"
                  type="number"
                  domain={[0, horizon]}
                  ticks={ticks}
                  tickFormatter={(y: number) => `Yr ${y}`}
                  tick={{ fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  domain={[yMin, yMax]}
                  tickFormatter={formatChartCurrency}
                  tick={{ fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                  width={64}
                />
                <Tooltip content={<JourneyTooltip />} />
                <ReferenceLine y={0} stroke="hsl(var(--muted-foreground))" strokeWidth={1.5} />
                {/* type="monotone" is d3's curveMonotoneX; breakevenYear is computed from that
                    same curve in strategyJourney.ts, so the dot sits exactly where the drawn
                    line crosses zero. Changing the curve type here breaks that alignment. */}
                <Area
                  type="monotone"
                  dataKey="position"
                  stroke={stroke}
                  strokeWidth={2.5}
                  fill={`url(#${gradId})`}
                  baseValue={0}
                  dot={false}
                  activeDot={{ r: 4 }}
                  isAnimationActive={false}
                />
                {!forPrint && j.breakevenYear !== null && j.breakevenYear > 0 && (
                  <ReferenceDot
                    x={j.breakevenYear}
                    y={0}
                    r={4}
                    fill={stroke}
                    stroke="hsl(var(--card))"
                    strokeWidth={2}
                    label={{ value: "Breakeven", position: "top", fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                  />
                )}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        );
      })}
    </div>
  );
}

// ── Shared detail section (used in both CPA view and expanded client view) ───
function DetailView({
  scenarios,
  baseScenario,
  barChartData,
  costVsReturnData,
  horizonYears,
  reportDate: _reportDate,
}: {
  scenarios: TaxScenario[];
  baseScenario: TaxScenario;
  barChartData: any[];
  costVsReturnData: any[];
  horizonYears: number;
  reportDate: string;
}) {
  // Bottom analysis card toggles between the existing cost-benefit bar chart and the new
  // 10-year Roth-vs-IRA projection. Defaults to 'cost' (no surprise for current users).
  const [chartView, setChartView] = useState<'cost' | 'projection'>('cost');
  // Future tax rate increase in percentage points (e.g. 0.05 = +5pp on top of baseline ETR).
  const [taxRateDelta, setTaxRateDelta] = useState<number>(0.05);
  // Which non-baseline scenario to project — the 3-line chart is per-strategy.
  const [projectionScenarioId, setProjectionScenarioId] = useState<string | null>(null);

  // Roth projection chart + toggle is gated behind 'fia_growth_strategies' AND the
  // Team Management FIA licence for this user — or a comparison that already carries
  // an FIA strategy, which keeps its chart after the licence is turned off. When the
  // gate is shut, only the Cost Benefit view renders and the toggle pill is hidden.
  const { isEnabled: isFlagEnabled } = useFeatureFlags();
  const fiaEnabled = useRothProjectionGate(scenarios);
  const effectiveChartView: 'cost' | 'projection' = fiaEnabled ? chartView : 'cost';
  // Journey cards are gated behind 'cost_benefit_journey' for a Dylan pilot — with the
  // flag off, the Cost Benefit view renders the legacy paired bars unchanged.
  const journeyEnabled = isFlagEnabled('cost_benefit_journey');

  // Scenarios eligible for the 10-year Roth projection — must have a Roth conversion.
  const projectionScenarios = scenarios.slice(1).filter((s) => (s.input.rothConversionAmount ?? 0) > 0);
  const activeProjectionScenario =
    projectionScenarios.find((s) => s.id === projectionScenarioId) ?? projectionScenarios[0] ?? null;
  const baselineEffectiveTaxRate = baseScenario.results.effectiveTaxRate ?? 0;

  // chart rework
  const shortLabel = (v: number) => {
    if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
    if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(0)}k`;
    return `$${v.toFixed(0)}`;
  };
  // Hide optional rows (CG/Div, AMT, NIIT, SE Tax) when every scenario is zero
  const visibleMetrics = METRICS.filter(m =>
    !m.optional || scenarios.some(s => m.getValue(s) !== 0)
  );

  return (
    <>
      <Card className="shadow-lg border-0 bg-gradient-to-br from-card to-card/80" data-tour="comparison-snapshot">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Current Year Snapshot</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={barChartData} barGap={4} margin={{ top: 25, right: 5, bottom: 5, left: 5 }}>
              <XAxis dataKey="name" tick={{ fontSize: 16 }} axisLine={false} tickLine={false} />
              <YAxis
                tick={{ fontSize: 15 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={formatChartCurrency}
                domain={[0, (dataMax: number) => dataMax * 1.1]}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend iconSize={12} wrapperStyle={{ fontSize: "15px" }} />
              {scenarios.map((s, i) => (
                <Bar
                  key={s.id}
                  dataKey={s.id}
                  name={i === 0 ? 'Baseline' : strategySuffix(s.input.name)}
                  fill={COMPARE_COLORS[i % COMPARE_COLORS.length]}
                  radius={[4, 4, 0, 0]}
                >
                  <LabelList
                    dataKey={s.id}
                    position="top"
                    formatter={shortLabel}
                    style={{ fontSize: 16, fontWeight: 600, fontFamily: "monospace", fill: "hsl(var(--foreground))" }}
                  />
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card className="shadow-lg border-0 bg-gradient-to-br from-card to-card/80" data-tour="comparison-cost-benefit">
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="text-lg">
              {effectiveChartView === 'cost'
                ? `Cost Benefit Analysis — ${horizonYears} Year Horizon`
                : 'Roth vs. IRA — 10 Year Projection'}
            </CardTitle>
            {fiaEnabled && (
              <div className="flex flex-wrap items-center gap-2 print:hidden">
                {effectiveChartView === 'projection' && projectionScenarios.length > 1 && (
                  <Select
                    value={activeProjectionScenario?.id ?? ''}
                    onValueChange={(v) => setProjectionScenarioId(v)}
                  >
                    <SelectTrigger className="h-8 text-xs w-[320px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {projectionScenarios.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {growthModelLabel(s.input.rothGrowthModel)} · {formatCompactCurrency(s.input.rothConversionAmount ?? 0)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {effectiveChartView === 'projection' && (
                  <div className="flex items-center gap-1.5">
                    <Label htmlFor="rothTaxDelta" className="text-[11px] text-muted-foreground whitespace-nowrap">
                      Future tax rate +
                    </Label>
                    <Input
                      id="rothTaxDelta"
                      type="number"
                      step="0.5"
                      min={0}
                      max={30}
                      value={(taxRateDelta * 100).toFixed(1)}
                      onChange={(e) => {
                        const pct = Number(e.target.value);
                        if (Number.isFinite(pct)) {
                          setTaxRateDelta(Math.max(0, Math.min(0.30, pct / 100)));
                        }
                      }}
                      className="h-8 w-20 text-xs font-mono"
                    />
                    <span className="text-[11px] text-muted-foreground">pp</span>
                  </div>
                )}
                <div className="inline-flex rounded-md border bg-background p-0.5">
                  <Button
                    type="button"
                    variant={effectiveChartView === 'cost' ? 'secondary' : 'ghost'}
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => setChartView('cost')}
                  >
                    Cost Benefit
                  </Button>
                  <Button
                    type="button"
                    variant={effectiveChartView === 'projection' ? 'secondary' : 'ghost'}
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => setChartView('projection')}
                    disabled={projectionScenarios.length === 0}
                    title={projectionScenarios.length === 0 ? 'Add a strategy with a Roth conversion to enable this view' : undefined}
                  >
                    Roth Projection
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {effectiveChartView === 'projection' && activeProjectionScenario ? (
            <RothProjectionChart
              scenario={activeProjectionScenario}
              baselineEffectiveTaxRate={baselineEffectiveTaxRate}
              taxRateDelta={taxRateDelta}
            />
          ) : effectiveChartView === 'projection' ? (
            <div className="flex items-center justify-center h-[380px] text-sm text-muted-foreground">
              No strategy with a Roth conversion to project.
            </div>
          ) : journeyEnabled ? (
            <StrategyJourneyCards scenarios={scenarios} horizon={horizonYears} />
          ) : (
          <ResponsiveContainer width="100%" height={380}>
            <BarChart data={costVsReturnData} barGap={8} margin={{ top: 40, right: 5, bottom: 5, left: 5 }}>
              <XAxis dataKey="scenario" tick={{ fontSize: 16 }} axisLine={false} tickLine={false} />
              <YAxis
                tick={{ fontSize: 15 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={formatChartCurrency}
                domain={[0, (dataMax: number) => dataMax * 1.1]}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                iconSize={12}
                wrapperStyle={{ fontSize: "15px" }}
                payload={[
                  { value: "Year 1 Cost Over Baseline", type: "square", color: COMPARE_COLORS[0] },
                  { value: `Year ${horizonYears} Gross Return (colored by scenario)`, type: "square", color: "hsl(var(--muted-foreground))" },
                ]}
              />
              <Bar dataKey="Year 1 Cost Over Baseline" fill={COMPARE_COLORS[0]} radius={[4, 4, 0, 0]}>
                <LabelList
                  dataKey="Year 1 Cost Over Baseline"
                  position="top"
                  formatter={shortLabel}
                  style={{ fontSize: 16, fontWeight: 600, fontFamily: "monospace", fill: "hsl(var(--foreground))" }}
                />
              </Bar>
              <Bar dataKey={`Year ${horizonYears} Gross Return`} radius={[4, 4, 0, 0]}>
                {costVsReturnData.map((_, i) => (
                  <Cell key={i} fill={COMPARE_COLORS[(i + 1) % COMPARE_COLORS.length]} />
                ))}
                <LabelList
                  dataKey={`Year ${horizonYears} Gross Return`}
                  position="top"
                  formatter={shortLabel}
                  style={{ fontSize: 16, fontWeight: 600, fontFamily: "monospace", fill: "hsl(var(--foreground))" }}
                />
              </Bar>
              <Customized
                component={(customProps: {
                  offset?: { left?: number; top?: number; width?: number };
                }) => {
                  const offset = customProps.offset;
                  if (!offset) return null;
                  const plotLeft = offset.left ?? 0;
                  const plotWidth = offset.width ?? 0;
                  const n = costVsReturnData.length;
                  if (n === 0 || plotWidth === 0) return null;
                  const slotWidth = plotWidth / n;
                  return (
                    <g>
                      {costVsReturnData.map((d, i) => {
                        const cx = plotLeft + (i + 0.5) * slotWidth;
                        const net = d.netBenefit as number;
                        const formatted = `${net >= 0 ? "+" : "-"}${formatChartCurrency(Math.abs(net))}`;
                        const color = net >= 0 ? "hsl(var(--success))" : "hsl(var(--destructive))";
                        return (
                          <text
                            key={d.scenario}
                            x={cx}
                            y={18}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            style={{ fontSize: 20, fontWeight: 700, fontFamily: "monospace", fill: color }}
                          >
                            {formatted}
                          </text>
                        );
                      })}
                    </g>
                  );
                }}
              />
            </BarChart>
          </ResponsiveContainer>
          )}
          {effectiveChartView === 'cost' && journeyEnabled ? (
            <p className="text-sm text-muted-foreground mt-2">
              Each card tracks one strategy's cumulative position vs. doing nothing, from its scenario's real payback
              schedule: the line starts down by the Year 1 cost over baseline (out-of-pocket net of that year's tax
              savings), alt investment cash follows its hypothetical schedule scaled to the investment, and estimated future taxes avoided on the Roth accrue in equal slices over the first {JOURNEY_ROTH_SPREAD_YEARS} years (assumes steady growth) and then hold.
              Red = still out of pocket; green = ahead of baseline.
            </p>
          ) : effectiveChartView === 'cost' ? (
            <p className="text-sm text-muted-foreground mt-2">
              Year 1 shows each strategy's total out-of-pocket cost above what the baseline would have cost. Year{" "}
              {horizonYears} shows projected total gross return (estimated future taxes avoided on Roth + alt investment
              gross return).
            </p>
          ) : (
            <p className="text-sm text-muted-foreground mt-2">
              Each line uses the strategy's selected Roth growth model over a 10-year horizon. Roth grows tax-free.
              The two IRA lines apply the baseline scenario's effective tax rate ({formatPercent(baselineEffectiveTaxRate)})
              and that same rate plus the selected future-rate increase at withdrawal. The alt investment is shown
              separately on the Cost Benefit view.
            </p>
          )}
        </CardContent>
      </Card>

      <Card className="shadow-lg border-0 bg-gradient-to-br from-card to-card/80" data-tour="comparison-detail">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Detailed Comparison</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="w-full">
            <table className="w-full caption-bottom text-sm">
              <TableHeader>
                <TableRow className="border-b-2">
                  <TableHead className="sticky left-0 top-12 bg-card z-20 min-w-[180px] font-semibold">Metric</TableHead>
                  {scenarios.map((s, i) => (
                    <TableHead key={s.id} className="sticky top-12 bg-card z-10 text-center min-w-[130px]">
                      <div className="font-semibold">{i === 0 ? 'Baseline' : strategySuffix(s.input.name)}</div>
                    </TableHead>
                  ))}
                  {scenarios.slice(1).map((s) => (
                    <TableHead key={`diff-${s.id}`} className="sticky top-12 bg-card z-10 text-center min-w-[120px]">
                      Δ {strategySuffix(s.input.name)}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {(() => {
                  const totalCols = 1 + scenarios.length + Math.max(0, scenarios.length - 1);
                  const firstFutureKey = visibleMetrics.find((m) => m.group === "future")?.key;
                  const rows: JSX.Element[] = [];
                  rows.push(
                    <TableRow key="section-current" className="bg-muted/40 hover:bg-muted/40">
                      <TableCell
                        colSpan={totalCols}
                        className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground py-2"
                      >
                        Current Year
                      </TableCell>
                    </TableRow>,
                  );
                  for (const m of visibleMetrics) {
                    if (m.key === firstFutureKey) {
                      rows.push(
                        <TableRow key="section-future" className="bg-muted/40 hover:bg-muted/40 border-t-2">
                          <TableCell
                            colSpan={totalCols}
                            className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground py-2"
                          >
                            Projected at end of Year {horizonYears}
                          </TableCell>
                        </TableRow>,
                      );
                    }
                    rows.push(
                      <TableRow key={m.key} className={m.highlight ? "bg-primary/5 font-medium" : ""}>
                        <TableCell className="sticky left-0 bg-card z-10 text-sm">{m.label}</TableCell>
                        {scenarios.map((s) => (
                          <TableCell key={s.id} className="text-center text-sm font-mono">
                            {formatVal(m.getValue(s), m.format)}
                          </TableCell>
                        ))}
                        {scenarios.slice(1).map((s) => (
                          <TableCell key={`diff-${s.id}`} className="text-center bg-muted/20">
                            <DiffCell
                              base={m.getValue(baseScenario)}
                              current={m.getValue(s)}
                              format={m.format}
                              direction={m.direction}
                            />
                          </TableCell>
                        ))}
                      </TableRow>,
                    );
                  }
                  return rows;
                })()}
                <TableRow className="bg-secondary/10 font-bold border-t-2">
                  <TableCell className="sticky left-0 bg-card z-10 text-sm font-bold">
                    Est. Future Value Economic Difference
                  </TableCell>
                  {scenarios.map((s, i) => {
                    if (i === 0) {
                      return (
                        <TableCell key={s.id} className="text-center text-sm font-mono text-muted-foreground">
                          —
                        </TableCell>
                      );
                    }
                    const oopDiff = s.results.totalEconomicCost - baseScenario.results.totalEconomicCost;
                    const fvEconDiff = s.results.hpeFutureValue + s.results.estimatedFutureTaxAvoided - oopDiff;
                    return (
                      <TableCell key={s.id} className="text-center text-sm font-mono">
                        <span className={fvEconDiff >= 0 ? "text-success" : "text-destructive"}>
                          {formatCurrency(fvEconDiff)}
                        </span>
                      </TableCell>
                    );
                  })}
                  {scenarios.slice(1).map((s) => {
                    const oopDiff = s.results.totalEconomicCost - baseScenario.results.totalEconomicCost;
                    const fvEconDiff = s.results.hpeFutureValue + s.results.estimatedFutureTaxAvoided - oopDiff;
                    return (
                      <TableCell key={`diff-${s.id}`} className="text-center bg-muted/20 font-mono text-sm">
                        <span className={`font-bold ${fvEconDiff >= 0 ? "text-success" : "text-destructive"}`}>
                          {formatCurrency(fvEconDiff)}
                        </span>
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableBody>
            </table>
          </div>
        </CardContent>
      </Card>
    </>
  );
}
